import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { Navbar } from "./components/layout/Navbar";
import { GameSidebar } from "./components/layout/GameSidebar";
import { useAuthStore } from "./store/authStore";
import { detectAndTrackInstall } from "./lib/analytics";

// Pages
import LandingPage from "./pages/LandingPage";
import LeaguePartnersPage from "./pages/LeaguePartnersPage";
import ContactPage from "./pages/ContactPage";
import BlogPage from "./pages/BlogPage";
import ArticlePage from "./pages/ArticlePage";
import LoginPage from "./pages/LoginPage";
import ParentBrowsePage from "./pages/ParentBrowsePage";
import HomePage from "./pages/HomePage";
import PreGamePage from "./pages/PreGamePage";
import LineupPage from "./pages/LineupPage";
import PitchingPage from "./pages/PitchingPage";
import StatsPage from "./pages/StatsPage";
import SprayChartsPage from "./pages/SprayChartsPage";
import ParentViewPage from "./pages/ParentViewPage";
import CoachesPage from "./pages/CoachesPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import SubscriptionCancelPage from "./pages/SubscriptionCancelPage";
import GameSummaryPage from "./pages/GameSummaryPage";
import SeasonStatsPage from "./pages/SeasonStatsPage";
import PlayerProfilePage from "./pages/PlayerProfilePage";
import GameSetupPage from "./pages/GameSetupPage";
import HighlightReelPage from "./pages/HighlightReelPage";
import TournamentPage from "./pages/TournamentPage";
import MessagesPage from "./pages/MessagesPage";
import AbbreviationKey from "./components/shared/AbbreviationKey";
import GameModePage from "./pages/GameModePage";
import LiveScorePage from "./pages/LiveScorePage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import UpgradePage from "./pages/UpgradePage";
import AdminPage from "./pages/AdminPage";
import WhosHotPage from "./pages/WhosHotPage";
import JoinTeamPage from "./pages/JoinTeamPage";
import OnboardingPage from "./pages/OnboardingPage";

// Protected route wrapper for coach-only pages
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isCoach, isAdmin, token } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Give zustand time to hydrate from localStorage
    const timer = setTimeout(() => setIsLoading(false), 100);
    return () => clearTimeout(timer);
  }, []);
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="w-8 h-8 border-4 border-green-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated || !token) {
    return <Navigate to="/login" replace />;
  }
  
  // Allow all authenticated users to access dashboard (they can create their first team)
  // Previously this blocked non-coaches, but new users need to access dashboard to create teams
  return children;
};

const AppContent = () => {
  const location = useLocation();
  const { isAuthenticated, isCoach, isAdmin, registeredAs } = useAuthStore();
  // Sidebar starts COLLAPSED on phones (≤768px) so the scoring area gets the full
  // viewport — otherwise the expanded sidebar (w-48 = 192px) eats half the screen
  // and the HOME team scoreboard card clips off the right edge. Coach can still
  // expand any time via the green chevron button.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });

  // Detect already-installed PWAs (catches iOS / "Add to Home Screen" users that the
  // appinstalled event misses). Fires once per device via localStorage.
  useEffect(() => {
    detectAndTrackInstall();
  }, []);
  
  // Pages that don't need navbar/sidebar
  const noNavbarPaths = ['/', '/login', '/parent-browse', '/parent-view', '/subscription', '/whos-hot', '/join', '/onboarding'];
  const isNoNavbar = noNavbarPaths.some(p => location.pathname.startsWith(p)) || 
                     location.pathname.startsWith('/parent-view/');
  
  const isCoachUser = isCoach || isAdmin || registeredAs === 'coach';
  
  // Pages that use the game sidebar (game-related pages)
  const gameSidebarPaths = ['/pregame', '/lineup', '/field', '/scoring', '/pitching', '/stats', '/spray-charts', '/game-summary', '/game-setup', '/messages', '/key', '/game-mode'];
  const showGameSidebar = gameSidebarPaths.some(p => location.pathname === p) && 
                          isAuthenticated && isCoachUser;
  
  // Show navbar for coaches OR admins (on dashboard and coaches page)
  const showNavbar = !isNoNavbar && !showGameSidebar && isAuthenticated && isCoachUser;

  return (
    <div className="min-h-screen bg-stone-100">
      {showNavbar && <Navbar />}
      {showGameSidebar && (
        <GameSidebar 
          collapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
      )}
      <div className={showGameSidebar ? (sidebarCollapsed ? 'ml-14' : 'ml-48') : ''}>
        <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/parent-browse" element={<ParentBrowsePage />} />
        <Route path="/parent-view/:teamId/:gameId" element={<ParentViewPage />} />
        <Route path="/highlights/:shareToken" element={<HighlightReelPage />} />
        <Route path="/live-score/:teamId/:gameId" element={<LiveScorePage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/game-summary/:teamId/:gameId" element={<GameSummaryPage />} />
        <Route path="/subscription" element={<UpgradePage />} />
        <Route path="/upgrade" element={<UpgradePage />} />
        <Route path="/subscription/success" element={<UpgradePage />} />
        <Route path="/subscription/cancel" element={<UpgradePage />} />
        <Route path="/admin" element={
          <ProtectedRoute><AdminPage /></ProtectedRoute>
        } />
        <Route path="/whos-hot/:teamId" element={<WhosHotPage />} />
        <Route path="/join/:teamId" element={<JoinTeamPage />} />
        <Route path="/league-partners" element={<LeaguePartnersPage />} />
        <Route path="/leagues" element={<LeaguePartnersPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<ArticlePage />} />
        
        {/* Protected coach routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute><HomePage /></ProtectedRoute>
        } />
        <Route path="/onboarding" element={
          <ProtectedRoute><OnboardingPage /></ProtectedRoute>
        } />
        <Route path="/pregame" element={
          <ProtectedRoute><PreGamePage /></ProtectedRoute>
        } />
        <Route path="/game-setup" element={
          <ProtectedRoute><GameSetupPage /></ProtectedRoute>
        } />
        <Route path="/lineup" element={
          <ProtectedRoute><LineupPage /></ProtectedRoute>
        } />
        <Route path="/field" element={<Navigate to="/game-mode" replace />} />
        <Route path="/scoring" element={<Navigate to="/game-mode" replace />} />
        <Route path="/pitching" element={
          <ProtectedRoute><PitchingPage /></ProtectedRoute>
        } />
        <Route path="/stats" element={
          <ProtectedRoute><StatsPage /></ProtectedRoute>
        } />
        <Route path="/spray-charts" element={
          <ProtectedRoute><SprayChartsPage /></ProtectedRoute>
        } />
        <Route path="/coaches" element={
          <ProtectedRoute><CoachesPage /></ProtectedRoute>
        } />
        <Route path="/game-summary" element={
          <ProtectedRoute><GameSummaryPage /></ProtectedRoute>
        } />
        <Route path="/season-stats" element={
          <ProtectedRoute><SeasonStatsPage /></ProtectedRoute>
        } />
        <Route path="/player-profile/:teamId/:playerId" element={
          <ProtectedRoute><PlayerProfilePage /></ProtectedRoute>
        } />
        <Route path="/tournaments" element={
          <ProtectedRoute><TournamentPage /></ProtectedRoute>
        } />
        <Route path="/messages" element={
          <ProtectedRoute><MessagesPage /></ProtectedRoute>
        } />
        <Route path="/key" element={
          <ProtectedRoute><AbbreviationKey variant="page" /></ProtectedRoute>
        } />
        <Route path="/game-mode" element={
          <ProtectedRoute><GameModePage /></ProtectedRoute>
        } />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <Toaster position="top-right" />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
